import React, { useEffect, useState } from "react";
import axios from "axios";
import { BeeLogo } from "./BeeLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Soft maintenance gate.
 * On mount, hits GET /api/health (which is always public and reports
 * { maintenance: true|false, message }).
 *
 * If maintenance:true, render a calm holding screen and BLOCK all routes.
 * If false (or health check fails open), render children normally.
 *
 * Never shows red errors. Never leaks "the server is down". Always soft.
 */
export const MaintenanceGate = ({ children }) => {
  const [state, setState] = useState("checking"); // "checking" | "open" | "down"
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/health`, {
          timeout: 8000,
        });
        if (cancelled) return;
        if (data?.maintenance) {
          setMessage(
            data.message ||
              "BeeNest is currently undergoing improvements and security updates. We'll be back soon."
          );
          setState("down");
        } else {
          setState("open");
        }
      } catch {
        // Fail open — if the health check itself fails, render the app and let
        // its existing SoftErrorBoundary + soft 401 handling take over. Better
        // than locking real parents out on a transient network blip.
        if (!cancelled) setState("open");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking") {
    // Honeycomb empty canvas while we ask — no spinner, calm
    return (
      <div
        className="min-h-screen bn-honeycomb"
        role="status"
        aria-live="polite"
        aria-label="Checking the hive"
        data-testid="maintenance-checking"
      />
    );
  }

  if (state === "down") {
    return <MaintenanceScreen message={message} />;
  }

  return children;
};

const MaintenanceScreen = ({ message }) => (
  <div
    className="min-h-screen bn-honeycomb flex items-center justify-center px-6 py-10"
    role="main"
    data-testid="maintenance-screen"
  >
    <div className="max-w-lg w-full text-center bn-card p-10 md:p-12 bn-fade">
      <div className="flex justify-center mb-6 bn-float" aria-hidden="true">
        <BeeLogo size={96} variant="portrait" />
      </div>
      <div
        className="text-xs font-semibold tracking-[0.22em] uppercase mb-3"
        style={{ color: "var(--bn-text-secondary)" }}
      >
        A quiet moment
      </div>
      <h1
        className="display text-3xl md:text-4xl font-semibold mb-4"
        style={{ color: "var(--bn-text-primary)" }}
      >
        BeeNest is resting briefly
      </h1>
      <p
        className="text-base leading-relaxed"
        style={{ color: "var(--bn-text-secondary)" }}
      >
        {message}
      </p>
      <div
        className="mt-8 text-xs italic"
        style={{ color: "var(--bn-text-secondary)" }}
      >
        © BeeNest by IM•BEE — Evelyn Villanueva
      </div>
    </div>
  </div>
);

export default MaintenanceGate;
